import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Grid,
  IconButton,
  Chip,
  Input,
  Sheet,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Option,
  Breadcrumbs,
  Link,
  Stack,
  Alert,
} from "@mui/joy";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../../components/common/ResponsiveTable";
import { useNavigate } from "react-router-dom";
import {
  Plus as AddIcon,
  FolderOpen as FolderIcon,
  FolderPlus as CreateFolderIcon,
  FileText as FormIcon,
  MagnifyingGlass as SearchIcon,
  DotsThreeVertical as MoreIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  Copy as DuplicateIcon,
  Code as CodeIcon,
  Archive as ArchiveIcon,
  CheckCircle as ActiveIcon,
  Circle as InactiveIcon,
  House as HomeIcon,
  CaretRight as ChevronRightIcon,
  ListBullets as ViewSubmissionsIcon,
} from "@phosphor-icons/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { formTemplates } from "../../lib/formTemplates";
import {
  formsApi,
  formFoldersApi,
  type Form,
  type FormFolder,
} from "../../lib/apiMarketing";

export const Forms: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showCreateFormModal, setShowCreateFormModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState<Form | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFormName, setNewFormName] = useState("");
  const [newFormDescription, setNewFormDescription] = useState("");
  const [page, setPage] = useState(1);
  const [template, setTemplate] = useState("Blank");
  const [embedCode, setEmbedCode] = useState<{
    direct_url: string;
    iframe: string;
    script: string;
  } | null>(null);
  const [editingFolder, setEditingFolder] = useState<FormFolder | null>(null);
  const [movingForm, setMovingForm] = useState<Form | null>(null);
  const [targetFolder, setTargetFolder] = useState<string>("root");
  const [folderName, setFolderName] = useState("");
  const [savingFolder, setSavingFolder] = useState(false);
  const [savedFilter, setSavedFilter] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("forms-saved-filter") || "null");
    } catch {
      return null;
    }
  });
  useEffect(() => {
    setPage(1);
  }, [currentFolder, searchQuery, selectedStatus]);

  const {
    data: allFolders = [],
    isError: foldersError,
    refetch: reloadFolders,
  } = useQuery({
    queryKey: ["form-folders"],
    queryFn: async () => {
      const rows: FormFolder[] = [];
      let nextPage = 1;
      while (true) {
        const result = await formFoldersApi.list({ page: nextPage });
        rows.push(...result.data.results);
        if (!result.data.next) return rows;
        nextPage += 1;
      }
    },
  });
  const folderPath: FormFolder[] = [];
  let ancestor = allFolders.find((folder) => folder.id === currentFolder);
  while (ancestor && !folderPath.some((folder) => folder.id === ancestor!.id)) {
    folderPath.unshift(ancestor);
    ancestor = allFolders.find((folder) => folder.id === ancestor!.parent);
  }

  // Fetch forms
  const {
    data: formsData,
    isLoading: formsLoading,
    isError: formsError,
    refetch: reloadForms,
  } = useQuery({
    queryKey: ["forms", currentFolder, selectedStatus, searchQuery, page],
    queryFn: () => {
      const params: any = { page };

      // Only include folder if it's set, otherwise show all unfiled forms
      if (currentFolder) {
        params.folder = currentFolder;
      }

      // Only include status if it's not "all"
      if (selectedStatus && selectedStatus !== "all") {
        params.status = selectedStatus;
      }

      // Only include search if there's actually a query
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      return formsApi.list(params);
    },
  });

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (data: Partial<FormFolder>) => formFoldersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-folders"] });
      setShowCreateFolderModal(false);
      setNewFolderName("");
      toast.success("Folder created successfully");
    },
    onError: () => {
      toast.error("Failed to create folder");
    },
  });

  // Create form mutation
  const createFormMutation = useMutation({
    mutationFn: (data: Partial<Form>) => formsApi.create(data),
    onSuccess: (newForm) => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      setShowCreateFormModal(false);
      setNewFormName("");
      setNewFormDescription("");
      toast.success("Form created successfully");
      // Navigate to form builder
      navigate(`/marketing/forms/${newForm.data.id}/edit`);
    },
    onError: () => {
      toast.error("Failed to create form");
    },
  });

  // Delete form mutation
  const deleteFormMutation = useMutation({
    mutationFn: (id: string) => formsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      toast.success("Form deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete form");
    },
  });

  // Toggle form status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => formsApi.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      toast.success("Form status updated");
    },
    onError: () => {
      toast.error("Failed to update form status");
    },
  });

  const handleCreateFolder = () => {
    createFolderMutation.mutate({
      name: newFolderName,
      parent: currentFolder || undefined,
    });
  };

  const handleCreateForm = () => {
    createFormMutation.mutate({
      name: newFormName,
      description: newFormDescription,
      folder: currentFolder || undefined,
      status: "draft",
      form_schema: {
        fields:
          formTemplates.find((item) => item.name === template)?.fields || [],
      },
      submit_button_text: "Submit",
      submit_action: "message",
      success_message: "Thank you for your submission!",
      email_notification_enabled: true,
      email_notification_to: [],
      create_contact: true,
    });
  };

  const handleDuplicateForm = async (form: Form) => {
    const newName = prompt(
      "Enter name for the duplicated form:",
      `${form.name} (Copy)`,
    );
    if (newName) {
      try {
        await formsApi.duplicate(form.id, {
          name: newName,
          folder: form.folder || undefined,
        });
        queryClient.invalidateQueries({ queryKey: ["forms"] });
        toast.success("Form duplicated successfully");
      } catch (error) {
        toast.error("Failed to duplicate form");
      }
    }
  };

  const folders = allFolders.filter(
    (folder) => (folder.parent || null) === currentFolder,
  );
  const showEmbed = async (form: Form) => {
    try {
      const result = await formsApi.getEmbedCode(form.id);
      setEmbedCode(result.data);
      setShowEmbedModal(form);
    } catch {
      toast.error("Unable to load embed code. Please retry.");
    }
  };
  const saveFolder = async () => {
    setSavingFolder(true);
    try {
      if (editingFolder)
        await formFoldersApi.update(editingFolder.id, {
          name: folderName,
          parent: targetFolder === "root" ? null : targetFolder,
        });
      if (movingForm)
        await formsApi.update(movingForm.id, {
          folder: targetFolder === "root" ? null : targetFolder,
        });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["form-folders"] }),
        queryClient.invalidateQueries({ queryKey: ["forms"] }),
      ]);
      setEditingFolder(null);
      setMovingForm(null);
    } catch {
      toast.error(
        "Unable to move or rename. Check the folder selection and retry.",
      );
    } finally {
      setSavingFolder(false);
    }
  };
  const forms = formsData?.data?.results || [];

  const formsColumns: ResponsiveColumn<Form>[] = [
    {
      key: "icon",
      header: "",
      mobileLabel: "",
      width: "40px",
      hideOnMobile: true,
      render: () => <FormIcon />,
    },
    {
      key: "name",
      sortValue: (row) => row.name,
      header: "Name",
      mobileVariant: "header",
      render: (form) => (
        <Box>
          <Link
            level="title-sm"
            onClick={() => navigate(`/marketing/forms/${form.id}/edit`)}
            sx={{ textDecoration: "none", cursor: "pointer" }}
          >
            {form.name}
          </Link>
          {form.description && (
            <Typography level="body-xs" textColor="text.secondary">
              {form.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "status",
      sortValue: (row) => row.status,
      header: "Status",
      width: "120px",
      render: (form) => (
        <Chip
          size="sm"
          color={
            form.status === "active"
              ? "success"
              : form.status === "draft"
                ? "neutral"
                : "warning"
          }
          startDecorator={
            form.status === "active" ? <ActiveIcon /> : <InactiveIcon />
          }
        >
          {form.status}
        </Chip>
      ),
    },
    {
      key: "submissions",
      sortValue: (row) => row.submissions_count,
      header: "Submissions",
      width: "120px",
      render: (form) => (
        <Link
          level="body-sm"
          onClick={() => navigate(`/marketing/forms/${form.id}/submissions`)}
          sx={{
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {form.submissions_count || 0}
        </Link>
      ),
    },
    {
      key: "updated_at",
      sortValue: (row) => (row.updated_at ? new Date(row.updated_at) : null),
      header: "Last Updated",
      width: "150px",
      render: (form) => (
        <Typography level="body-sm">
          {format(new Date(form.updated_at), "MMM d, yyyy h:mm a")}
        </Typography>
      ),
    },
    {
      key: "updated_by",
      sortValue: (row) => row.created_by_name,
      header: "Updated By",
      width: "150px",
      render: (form) => (
        <Typography level="body-sm">
          {form.created_by_name || "Unknown"}
        </Typography>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header — compact: the marketing shell already renders the page title */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Breadcrumbs separator={<ChevronRightIcon />} sx={{ p: 0 }}>
            <Link
              color="neutral"
              onClick={() => setCurrentFolder(null)}
              sx={{ cursor: "pointer" }}
            >
              <HomeIcon style={{ marginRight: 4 }} />
              All Forms
            </Link>
            {folderPath.map((folder) => (
              <Link
                key={folder.id}
                color="neutral"
                onClick={() => setCurrentFolder(folder.id)}
                sx={{ cursor: "pointer" }}
              >
                {folder.name}
              </Link>
            ))}
          </Breadcrumbs>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              startDecorator={<CreateFolderIcon />}
              variant="outlined"
              color="primary"
              onClick={() => setShowCreateFolderModal(true)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Create Folder
            </Button>
            <Button
              startDecorator={<AddIcon />}
              onClick={() => setShowCreateFormModal(true)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Add Form
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <CollapsibleFilters><Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Input
            placeholder="Search forms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startDecorator={<SearchIcon />}
            sx={{ flex: 1, maxWidth: { sm: 400 } }}
          />
          <Select
            value={selectedStatus}
            onChange={(_, value) => setSelectedStatus(value as string)}
            sx={{ minWidth: { sm: 150 } }}
          >
            <Option value="all">All Status</Option>
            <Option value="draft">Draft</Option>
            <Option value="active">Active</Option>
            <Option value="archived">Archived</Option>
          </Select>
        </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ pt: 1, flexWrap: "wrap" }}
      >
        <Button
          size="sm"
          variant="plain"
          onClick={() => {
            const filter = { searchQuery, selectedStatus };
            localStorage.setItem("forms-saved-filter", JSON.stringify(filter));
            setSavedFilter(filter);
            toast.success("Filter saved on this browser");
          }}
        >
          Save filter
        </Button>
        {savedFilter && (
          <Button
            size="sm"
            variant="plain"
            onClick={() => {
              setSearchQuery(savedFilter.searchQuery || "");
              setSelectedStatus(savedFilter.selectedStatus || "all");
            }}
          >
            Apply saved filter
          </Button>
        )}
      </Stack>
</CollapsibleFilters>
      </Box>

      {(formsError || foldersError) && (
        <Alert
          color="danger"
          endDecorator={
            <Button
              onClick={() => {
                reloadForms();
                reloadFolders();
              }}
            >
              Retry
            </Button>
          }
        >
          Unable to load forms or folders.
        </Alert>
      )}
      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 } }}>
        {/* Folders */}
        {folders.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Folders
            </Typography>
            <Grid container spacing={2}>
              {folders.map((folder: FormFolder) => (
                <Grid xs={12} sm={6} md={4} lg={3} key={folder.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: "background.level1" },
                    }}
                    onClick={() => setCurrentFolder(folder.id)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        aria-label={`Manage ${folder.name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditingFolder(folder);
                          setFolderName(folder.name);
                          setTargetFolder(folder.parent || "root");
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <FolderIcon color="var(--joy-palette-primary-500)" />
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-md">{folder.name}</Typography>
                        {folder.forms_count !== undefined && (
                          <Typography
                            level="body-sm"
                            textColor="text.secondary"
                          >
                            {folder.forms_count} forms
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Forms Table */}
        {formsLoading ? (
          <Sheet
            variant="outlined"
            sx={{ borderRadius: "sm", p: 4, textAlign: "center" }}
          >
            <Typography>Loading forms...</Typography>
          </Sheet>
        ) : (
          <ResponsiveTable<Form>
            rows={forms}
            getRowKey={(f) => f.id}
            columns={formsColumns}
            rowActions={(form) => (
              <Dropdown>
                <MenuButton
                  slots={{ root: IconButton }}
                  slotProps={{ root: { size: "sm", variant: "plain" } }}
                >
                  <MoreIcon />
                </MenuButton>
                <Menu placement="bottom-end">
                  <MenuItem
                    onClick={() => navigate(`/marketing/forms/${form.id}/edit`)}
                  >
                    <EditIcon /> Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      navigate(`/marketing/forms/${form.id}/submissions`)
                    }
                  >
                    <ViewSubmissionsIcon /> View Submissions
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setMovingForm(form);
                      setTargetFolder(form.folder || "root");
                    }}
                  >
                    Move to folder
                  </MenuItem>
                  <MenuItem onClick={() => handleDuplicateForm(form)}>
                    <DuplicateIcon /> Duplicate
                  </MenuItem>
                  <MenuItem onClick={() => showEmbed(form)}>
                    <CodeIcon /> Get Embed Code
                  </MenuItem>
                  <MenuItem
                    onClick={() => toggleStatusMutation.mutate(form.id)}
                  >
                    {form.status === "active" ? (
                      <ArchiveIcon />
                    ) : (
                      <ActiveIcon />
                    )}
                    {form.status === "active" ? "Deactivate" : "Activate"}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (
                        confirm("Are you sure you want to delete this form?")
                      ) {
                        deleteFormMutation.mutate(form.id);
                      }
                    }}
                    color="danger"
                  >
                    <DeleteIcon /> Delete
                  </MenuItem>
                </Menu>
              </Dropdown>
            )}
            actionsHeader=""
            actionsWidth="60px"
            emptyState={
              <Sheet
                variant="outlined"
                sx={{ borderRadius: "sm", p: 4, textAlign: "center" }}
              >
                <Typography>No forms found</Typography>
              </Sheet>
            }
          />
        )}
      </Box>

      <Stack direction="row" spacing={2} sx={{ p: 2 }} alignItems="center">
        <Button
          disabled={page === 1 || formsLoading}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </Button>
        <Typography>
          {formsData?.data.count || 0} forms · Page {page}
        </Typography>
        <Button
          disabled={!formsData?.data.next || formsLoading}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Stack>
      <Modal
        open={!!editingFolder || !!movingForm}
        onClose={() => {
          setEditingFolder(null);
          setMovingForm(null);
        }}
      >
        <ModalDialog>
          <ModalClose />
          <DialogTitle>
            {editingFolder ? "Manage folder" : "Move form"}
          </DialogTitle>
          {editingFolder && (
            <FormControl>
              <FormLabel>Folder name</FormLabel>
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
              />
            </FormControl>
          )}
          <FormControl>
            <FormLabel>Destination folder</FormLabel>
            <Select
              value={targetFolder}
              onChange={(_, value) => setTargetFolder(value || "root")}
            >
              <Option value="root">Root / unfiled</Option>
              {allFolders
                .filter((folder) => folder.id !== editingFolder?.id)
                .map((folder) => (
                  <Option key={folder.id} value={folder.id}>
                    {folder.name}
                  </Option>
                ))}
            </Select>
          </FormControl>
          <Button
            loading={savingFolder}
            disabled={!!editingFolder && !folderName.trim()}
            onClick={saveFolder}
          >
            Save
          </Button>
        </ModalDialog>
      </Modal>
      {/* Create Folder Modal */}
      <Modal
        open={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
      >
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Create Folder</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Folder Name</FormLabel>
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  autoFocus
                />
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="plain"
                  onClick={() => setShowCreateFolderModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                >
                  Create Folder
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>

      {/* Create Form Modal */}
      <Modal
        open={showCreateFormModal}
        onClose={() => setShowCreateFormModal(false)}
      >
        <ModalDialog sx={{ width: "90%", maxWidth: 560 }}>
          <ModalClose />
          <DialogTitle>Create Form</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Starting template</FormLabel>
                <Select
                  value={template}
                  onChange={(_, value) => {
                    setTemplate(value || "Blank");
                    if (!newFormName)
                      setNewFormName(value === "Blank" ? "" : value || "");
                  }}
                >
                  <Option value="Blank">Blank</Option>
                  {formTemplates.map((item) => (
                    <Option value={item.name} key={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Form Name</FormLabel>
                <Input
                  value={newFormName}
                  onChange={(e) => setNewFormName(e.target.value)}
                  placeholder="Enter form name"
                  autoFocus
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description (optional)</FormLabel>
                <Textarea
                  value={newFormDescription}
                  onChange={(e) => setNewFormDescription(e.target.value)}
                  placeholder="Enter form description"
                  minRows={2}
                />
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="plain"
                  onClick={() => setShowCreateFormModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateForm}
                  disabled={!newFormName.trim()}
                >
                  Create Form
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>

      {/* Embed Code Modal */}
      {showEmbedModal && (
        <Modal open={true} onClose={() => setShowEmbedModal(null)}>
          <ModalDialog size="lg">
            <ModalClose />
            <DialogTitle>Embed Form</DialogTitle>
            <DialogContent>
              <Typography level="body-md" sx={{ mb: 2 }}>
                Choose how you want to embed "{showEmbedModal.name}" on your
                website:
              </Typography>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Auto-resizing script</FormLabel>
                  <Textarea
                    value={embedCode?.script || ""}
                    readOnly
                    minRows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Direct Link</FormLabel>
                  <Input value={embedCode?.direct_url || ""} readOnly />
                </FormControl>
                <FormControl>
                  <FormLabel>Inline (Recommended)</FormLabel>
                  <Textarea
                    value={embedCode?.iframe || ""}
                    readOnly
                    minRows={3}
                  />
                </FormControl>
              </Stack>
            </DialogContent>
          </ModalDialog>
        </Modal>
      )}
    </Box>
  );
};
